import React, { useState } from 'react';

interface TruppForHandover {
  id: number;
  name: string;
  aufgabe: string;
  standort: string;
  supervisorName?: string;
  creatorID?: number;
  outOfService?: boolean;
}

interface TruppHandoverModalProps {
  open: boolean;
  /** Name des bisherigen Truppüberwachers (z.B. "Anna-Lena Salathe") */
  previousUserName: string;
  /** Name des neuen Truppüberwachers (z.B. "Markus Müller") */
  newUserName: string;
  /** Alle Trupps, die aktuell dem bisherigen Benutzer gehören */
  trupps: TruppForHandover[];
  /** Callback wenn der Benutzer die Übergabe bestätigt */
  onConfirm: (selectedTruppIds: number[]) => void;
  /** Callback wenn der Benutzer abbricht (Benutzerwechsel wird nicht durchgeführt) */
  onCancel: () => void;
}

const TruppHandoverModal: React.FC<TruppHandoverModalProps> = ({
  open,
  previousUserName,
  newUserName,
  trupps,
  onConfirm,
  onCancel
}) => {
  // Standardmässig alle Trupps ausgewählt (= alle übernehmen)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(
    new Set(trupps.map(t => t.id))
  );

  // Reset selection wenn sich die Trupps ändern
  React.useEffect(() => {
    setSelectedIds(new Set(trupps.map(t => t.id)));
  }, [trupps]);

  if (!open || trupps.length === 0) return null;

  const toggleTrupp = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(trupps.map(t => t.id)));
  const selectNone = () => setSelectedIds(new Set());

  const activeTrupps = trupps.filter(t => !t.outOfService);
  const outOfServiceTrupps = trupps.filter(t => t.outOfService);

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
      background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2100
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: 28, minWidth: 340, maxWidth: 500, width: '90vw',
        maxHeight: '85vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 8px 32px rgba(0,0,0,0.25)'
      }}>
        <h3 style={{ margin: '0 0 4px 0', color: '#333', fontSize: 20, textAlign: 'center' }}>
          Trupp-Übergabe
        </h3>
        <p style={{ color: '#666', fontSize: 14, margin: '0 0 16px 0', textAlign: 'center', lineHeight: 1.5 }}>
          <strong>{previousUserName}</strong> hat {trupps.length} Trupp{trupps.length !== 1 ? 's' : ''}. 
          Welche Trupps soll <strong>{newUserName}</strong> übernehmen?
        </p>

        {/* Info-Box */}
        <div style={{
          background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8,
          padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#795548', lineHeight: 1.4
        }}>
          <strong>Hinweis:</strong> Nicht ausgewählte Trupps bleiben bei <strong>{previousUserName}</strong> und 
          werden angezeigt, wenn sich {previousUserName} auf einem anderen Gerät einloggt.
        </div>

        {/* Alle/Keine auswählen */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <button onClick={selectAll} style={{
            background: selectedIds.size === trupps.length ? '#c00' : '#f5f5f5',
            color: selectedIds.size === trupps.length ? '#fff' : '#333',
            border: 'none', borderRadius: 6, padding: '6px 14px',
            fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s'
          }}>Alle übernehmen</button>
          <button onClick={selectNone} style={{
            background: selectedIds.size === 0 ? '#c00' : '#f5f5f5',
            color: selectedIds.size === 0 ? '#fff' : '#333',
            border: 'none', borderRadius: 6, padding: '6px 14px',
            fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s'
          }}>Keine übernehmen</button>
        </div>

        {/* Trupp-Liste */}
        <div style={{
          flex: 1, overflowY: 'auto', marginBottom: 16, maxHeight: '40vh',
          border: '1px solid #eee', borderRadius: 8
        }}>
          {/* Aktive Trupps */}
          {activeTrupps.map(trupp => (
            <div
              key={trupp.id}
              onClick={() => toggleTrupp(trupp.id)}
              style={{
                padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0',
                display: 'flex', alignItems: 'center', gap: 12,
                background: selectedIds.has(trupp.id) ? '#fff5f5' : 'transparent',
                transition: 'background 0.15s'
              }}
            >
              {/* Checkbox */}
              <div style={{
                width: 24, height: 24, borderRadius: 6, flexShrink: 0,
                border: selectedIds.has(trupp.id) ? '2px solid #c00' : '2px solid #ccc',
                background: selectedIds.has(trupp.id) ? '#c00' : '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.15s'
              }}>
                {selectedIds.has(trupp.id) && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                )}
              </div>

              {/* Trupp-Info */}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#333' }}>
                  {trupp.name}
                </div>
                <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                  {trupp.aufgabe}{trupp.standort ? ` | ${trupp.standort}` : ''}
                </div>
              </div>

              {/* Status-Badge */}
              <div style={{
                background: '#e8f5e9', color: '#2e7d32', fontSize: 11,
                padding: '2px 8px', borderRadius: 10, fontWeight: 600
              }}>
                Aktiv
              </div>
            </div>
          ))}

          {/* Ausser Dienst Trupps */}
          {outOfServiceTrupps.map(trupp => (
            <div
              key={trupp.id}
              onClick={() => toggleTrupp(trupp.id)}
              style={{
                padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0',
                display: 'flex', alignItems: 'center', gap: 12,
                background: selectedIds.has(trupp.id) ? '#fafafa' : 'transparent',
                opacity: 0.7, transition: 'background 0.15s'
              }}
            >
              <div style={{
                width: 24, height: 24, borderRadius: 6, flexShrink: 0,
                border: selectedIds.has(trupp.id) ? '2px solid #c00' : '2px solid #ccc',
                background: selectedIds.has(trupp.id) ? '#c00' : '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.15s'
              }}>
                {selectedIds.has(trupp.id) && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                )}
              </div>

              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#888' }}>
                  {trupp.name}
                </div>
                <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>
                  {trupp.aufgabe}{trupp.standort ? ` | ${trupp.standort}` : ''}
                </div>
              </div>

              <div style={{
                background: '#f5f5f5', color: '#999', fontSize: 11,
                padding: '2px 8px', borderRadius: 10, fontWeight: 600
              }}>
                Ausser Dienst
              </div>
            </div>
          ))}
        </div>

        {/* Zusammenfassung */}
        <div style={{
          background: '#f5f5f5', borderRadius: 8, padding: '10px 14px',
          marginBottom: 16, fontSize: 13, color: '#555', textAlign: 'center'
        }}>
          <strong>{selectedIds.size}</strong> von {trupps.length} Trupps werden an <strong>{newUserName}</strong> übergeben.
          {trupps.length - selectedIds.size > 0 && (
            <span> {trupps.length - selectedIds.size} Trupp{trupps.length - selectedIds.size !== 1 ? 's' : ''} {trupps.length - selectedIds.size !== 1 ? 'bleiben' : 'bleibt'} bei <strong>{previousUserName}</strong>.</span>
          )}
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between' }}>
          <button onClick={onCancel} style={{
            background: '#eee', border: 'none', borderRadius: 8, padding: '12px 20px',
            fontSize: 15, fontWeight: 600, cursor: 'pointer', color: '#555', flex: 1
          }}>Abbrechen</button>
          <button
            onClick={() => onConfirm(Array.from(selectedIds))}
            style={{
              background: '#c00', border: 'none', borderRadius: 8, padding: '12px 20px',
              fontSize: 15, fontWeight: 700, cursor: 'pointer', color: '#fff', flex: 1
            }}
          >Übergabe bestätigen</button>
        </div>
      </div>
    </div>
  );
};

export default TruppHandoverModal;

